import { useMemo, useState } from "react";
import { chipColor } from "@/lib/apptDisplay";
import { ServiceColorRail } from "@/components/calendar/ServiceColors";
import { colorSwatchHex } from "@/lib/vehicleCatalog";

const HOURS = Array.from({ length: 15 }, (_, i) => i + 7); // 7..21
const SLOT_HEIGHT = 48; // px per hour
const MINUTES_PER_HOUR = 60;

function layoutAppointments(list, dayStart, dayEnd) {
  // Filter appointments overlapping the day
  const items = list
    .filter((a) => new Date(a.end) > dayStart && new Date(a.start) < dayEnd)
    .map((a) => {
      const s = new Date(Math.max(new Date(a.start).getTime(), dayStart.getTime()));
      const e = new Date(Math.min(new Date(a.end).getTime(), dayEnd.getTime()));
      return { a, s, e };
    })
    .sort((x, y) => x.s - y.s);

  // Group overlapping into columns
  const columns = [];
  const result = [];
  for (const it of items) {
    let placed = false;
    for (let c = 0; c < columns.length; c++) {
      if (columns[c] <= it.s.getTime()) {
        columns[c] = it.e.getTime();
        result.push({ ...it, col: c });
        placed = true;
        break;
      }
    }
    if (!placed) {
      columns.push(it.e.getTime());
      result.push({ ...it, col: columns.length - 1 });
    }
  }
  const total = Math.max(1, columns.length);
  return result.map((r) => ({ ...r, cols: total }));
}

export function DayColumn({ date, appointments, onApptClick, onSlotClick, movingAppointment, showHeader = false }) {
  const dayStart = useMemo(() => { const d = new Date(date); d.setHours(0, 0, 0, 0); return d; }, [date]);
  const dayEnd = useMemo(() => { const d = new Date(dayStart); d.setDate(d.getDate() + 1); return d; }, [dayStart]);
  const gridStart = useMemo(() => { const d = new Date(dayStart); d.setHours(HOURS[0], 0, 0, 0); return d; }, [dayStart]);
  const gridEnd = useMemo(() => { const d = new Date(dayStart); d.setHours(HOURS[HOURS.length - 1] + 1, 0, 0, 0); return d; }, [dayStart]);

  const laid = layoutAppointments(appointments, dayStart, dayEnd);

  const [now, setNow] = useState(new Date());
  useMemo(() => { const t = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(t); }, []);
  const isToday = dayStart.toDateString() === new Date().toDateString();
  const dow = dayStart.getDay();
  const isWeekend = dow === 0 || dow === 6;
  const nowTopPx = isToday && now >= gridStart && now <= gridEnd
    ? ((now - gridStart) / 3600000) * SLOT_HEIGHT
    : null;

  const handleSlotClick = (hour) => {
    const s = new Date(dayStart); s.setHours(hour, 0, 0, 0);
    const e = new Date(s); e.setHours(s.getHours() + 1);
    onSlotClick?.(s, e);
  };

  return (
    <div className={`flex-1 min-w-0 relative border-r border-border/60 last:border-r-0 ${isToday ? "bg-emerald-50/35 dark:bg-emerald-950/10" : (isWeekend ? "bg-amber-50/70 dark:bg-amber-900/10" : "")}`}>
      {showHeader && (
        <div className={`text-center text-xs py-2 border-b border-border/60 ${isToday ? "bg-emerald-50 text-emerald-800 font-semibold border-b-2 border-b-emerald-500" : (isWeekend ? "bg-amber-50/70 dark:bg-amber-900/10 text-muted-foreground" : "text-muted-foreground")}`}>
          <div className="uppercase tracking-widest">{date.toLocaleDateString("de-DE", { weekday: "short" })}</div>
          <div className={`mx-auto mt-0.5 flex h-7 w-7 items-center justify-center rounded-full text-base font-display font-semibold ${isToday ? "bg-emerald-600 text-white shadow-sm ring-2 ring-emerald-200" : "text-foreground"}`}>{date.getDate()}</div>
        </div>
      )}
      <div
        className="relative"
        style={{ height: HOURS.length * SLOT_HEIGHT }}
      >
        {HOURS.map((h, i) => (
          <button
            key={h}
            type="button"
            data-testid={`slot-${date.toISOString().slice(0,10)}-${h}`}
            title={movingAppointment ? `Termin auf ${String(h).padStart(2, "0")}:00 Uhr verschieben` : `Neuen Termin um ${String(h).padStart(2, "0")}:00 Uhr anlegen`}
            onClick={() => handleSlotClick(h)}
            className={`absolute left-0 right-0 border-b border-border/40 transition-colors ${movingAppointment ? "hover:bg-blue-100/80 cursor-crosshair" : "hover:bg-muted/40"}`}
            style={{ top: i * SLOT_HEIGHT, height: SLOT_HEIGHT }}
          />
        ))}
        {nowTopPx !== null && <div className="now-line" style={{ top: nowTopPx }} />}
        {laid.map(({ a, s, e, col, cols }) => {
          const startMin = Math.max(0, (s - gridStart) / 60000);
          const endMin = Math.min(HOURS.length * 60, (e - gridStart) / 60000);
          const top = (startMin / 60) * SLOT_HEIGHT;
          const height = Math.max(20, ((endMin - startMin) / 60) * SLOT_HEIGHT - 2);
          const widthPct = 100 / cols;
          const leftPct = col * widthPct;
          const startHM = new Date(a.start).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
          const endHM = new Date(a.end).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
          // Multi-day continuation flags — used to indicate the appointment
          // extends beyond this day, so the user knows the block is a segment.
          const startsBeforeToday = new Date(a.start) < dayStart;
          const endsAfterToday = new Date(a.end) > dayEnd;
          const vehicle = [a.car_model, a.vehicle_year ? `Bj. ${a.vehicle_year}` : "", a.plate].filter(Boolean).join(" · ");
          return (
            <div
              key={a.id}
              onClick={(ev) => { ev.stopPropagation(); onApptClick?.(a); }}
              data-testid={`appt-${a.id}`}
              title="Termin auswählen und Aktionen anzeigen"
              className={`appointment-block absolute rounded-md shadow-sm px-2.5 py-1.5 overflow-hidden cursor-pointer hover:shadow-md transition-shadow flex flex-col ${movingAppointment?.id === a.id ? "ring-2 ring-blue-600 ring-inset" : ""}`}
              style={{
                top, height,
                left: `calc(${leftPct}% + 2px)`,
                width: `calc(${widthPct}% - 4px)`,
                backgroundColor: `color-mix(in srgb, ${chipColor(a)} 22%, hsl(var(--card)))`,
                color: "hsl(var(--foreground))",
              }}
            >
              <ServiceColorRail appointment={a} />
              <div className="text-[11px] font-mono tabular-nums font-semibold leading-tight truncate">
                {startsBeforeToday && <span className="text-muted-foreground">↑ </span>}
                {startHM}<span className="text-muted-foreground">–</span>{endHM}
                {endsAfterToday && <span className="text-muted-foreground"> ↓</span>}
              </div>
              <div className="text-[12px] font-semibold leading-tight truncate mt-0.5">{a.customer_name}</div>
              {height > 46 && a.service_name && (
                <div className="text-[10px] text-muted-foreground truncate leading-tight mt-0.5">{a.service_name}</div>
              )}
              {height > 62 && vehicle && (
                <div className="flex items-center gap-1 text-[10px] text-muted-foreground truncate leading-tight mt-0.5">
                  {a.vehicle_color && <span className="h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-black/10" style={{ background: colorSwatchHex(a.vehicle_color) }} title={a.vehicle_color} />}
                  <span className="truncate">{vehicle}</span>
                </div>
              )}
              {height > 68 && vehicle && (
                <div className="text-[10px] text-muted-foreground/90 truncate leading-tight">{vehicle}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function DayView({ date, appointments, onApptClick, onSlotClick, movingAppointment }) {
  return (
    <div className="flex">
      <div className="w-14 shrink-0 pt-0">
        <div className="h-0" />
        {HOURS.map((h) => (
          <div key={h} className="text-[10px] text-muted-foreground text-right pr-2 border-b border-border/40" style={{ height: SLOT_HEIGHT }}>
            <span className="-translate-y-2 inline-block">{String(h).padStart(2, "0")}:00</span>
          </div>
        ))}
      </div>
      <DayColumn date={date} appointments={appointments} onApptClick={onApptClick} onSlotClick={onSlotClick} movingAppointment={movingAppointment} />
    </div>
  );
}

export { HOURS, SLOT_HEIGHT };
